import PropTypes from 'prop-types';
// @flow

import React, { Component } from 'react';
import _ from 'lodash';
import I18n from 'PMT/i18n/I18n';

import ApiConstants from 'PMT/constants/ApiConstants';

import RestaurantStore from 'stores/RestaurantStore';
import MobileAppInteractor from 'utils/MobileAppInteractor';

import {
  CurrencyUtils,
  ViewUtils,
  Logger,
  PhoneUtils,
  OrderViewUtils,
  GmapUtils
} from 'PMT/utils';

// -- entities
import { Order } from 'PMT/entities/order';
import UserAddress from 'PMT/entities/user/UserAddress';

// -- views
import SvgIcon from 'PMT/components/SvgIcon';
import { OrderStatusLabel, OrderStatusStepper } from 'components/order';

export default class OrderDetailInfoView extends Component {

  static propTypes = {
    order: PropTypes.instanceOf(Order).isRequired,

    onSave: PropTypes.func.isRequired,

    onPrintOrder: PropTypes.func.isRequired,

    currencyCode: PropTypes.string,

  };

  state = {
    order: this.props.order.copy()
  };

  get order(): Order {
    return this.state.order;
  }

  get currencyCode(): string {
    return this.props.currencyCode;
  }

  //
  // View events
  //

  onPrintOrder = () => {
    this.props.onPrintOrder(this.order);
  }

  onChangeStatus = (newStatus) => {
    const order: Order = this.order;
    order.status = newStatus;
    this.props.onSave(order);
  };

  onDisplayDeliveryAddressOnGoogleMap = () => {
    // const url = GmapUtils.getGoogleMapsUrl(this.order.address.geoPt, 12);
    const url = GmapUtils.getGoogleMapsDirectionUrl(
      this.order.address.geoPt,
      RestaurantStore.getRestaurant().geoPt,
      12
    );
    window.open(url, '_blank');
  };


  //
  // Renderers
  //

  renderOnSiteClientInfo() {
    return (
      <div className="order_detail_info_view__client_info__on_site">
        <div className="order_detail_info_view__client_info__order_mode_icon">
          {!this.order.isOnBar() &&
            <SvgIcon
              icon={SvgIcon.ORDER_ON_SITE}
            />
          }
          {this.order.isOnBar() &&
            <SvgIcon
              icon={SvgIcon.ORDER_AT_BAR}
            />
          }
        </div>
        {!_.isEmpty(this.order.getTableNumberString()) &&
          <div className="order_detail_info_view__client_info__table_number">
            Table : {this.order.getTableNumberString()}
          </div>
        }
        {!_.isEmpty(this.order.getFormattedDueDate()) &&
          <div className="order_detail_info_view__client_info__pickup_date">
            {I18n.tr('components.order.detail.arrival_date')} {this.order.getFormattedDueDate()}
          </div>
        }
      </div>
    );
  }

  renderTakeAwayClientInfo() {
    return (
      <div className="order_detail_info_view__client_info__take_away">
        <div className="order_detail_info_view__client_info__order_mode_icon">
          <SvgIcon
            icon={SvgIcon.ORDER_TAKE_AWAY}
          />
        </div>
        <div className="order_detail_info_view__client_info__pickup_date">
          {I18n.tr('components.order.detail.pickup_date')} {this.order.getFormattedDueDate()}
        </div>
      </div>
    );
  }

  renderDeliveryClientInfo() {
    return (
      <div className="order_detail_info_view__client_info__delivery">
        <div className="order_detail_info_view__client_info__order_mode_icon">
          <SvgIcon
            icon={SvgIcon.ORDER_DELIVERY}
          />
        </div>
        <div className="order_detail_info_view__client_info__pickup_date">
          {I18n.tr('components.order.detail.delivery_date')} {this.order.getFormattedDueDate()}
        </div>
      </div>
    );
  }

  renderHeaderMode() {
    switch (this.order.mode) {
      case Order.OrderMode.ON_SITE:
        return this.renderOnSiteClientInfo();
      case Order.OrderMode.TAKE_AWAY:
        return this.renderTakeAwayClientInfo();
      case Order.OrderMode.DELIVERY:
        return this.renderDeliveryClientInfo();
      default:
        Logger.warning('ORDER', `Invalid order mode ${this.order.mode}`)
      return (null);
    }
  }

  renderClientInfo() {
    return (
      <div>
        <h3>{I18n.tr('components.order.detail.order_number')}{this.order.getOrderNumber()}</h3>
        {this.order.source.platform === ApiConstants.Platform.KIOSK ? (
          <p>
            {!_.isEmpty(this.order.source.identifier) &&
              `${I18n.tr('components.order.detail.kiosk_number')} ${this.order.source.identifier}`
            }
          </p>
        ) : (
          <div>
            <div className="order_detail_info_view__client_info__client_name">
              {!_.isNull(this.order.user) &&
                `${this.order.user.firstName || ''} ${this.order.user.lastName || ''}`
              }
            </div>
            <div className="order_detail_info_view__client_info__client_phone">
              {!_.isNull(this.order.user) &&
                <a
                  href={`tel:${this.order.user.phone}`}
                  className="link"
                >
                  {PhoneUtils.format(this.order.user.phone)}
                </a>
              }
            </div>
          </div>
        )}
      </div>
    );
  }

  renderDeliveryAddress() {
    if (!this.order.isDeliveryMode()) {
      return (null);
    }

    const address: UserAddress = this.order.address;

    if (_.isNil(address)) { // handle old orders with no address
      return (null);
    }

    return (
      <div className="order_detail_info_view__delivery_address">
        <div
          className="pointer"
          onClick={this.onDisplayDeliveryAddressOnGoogleMap}
        >
          {address.street}
          <br />
          {address.postCode} - {address.city}
        </div>
          <br />
        {address.complement}
        <br />
        {address.instructions}
      </div>
    );
  }

  renderComment() {
    if (_.isEmpty(this.order.comment)) {
      return (null);
    }

    return (
      <div className="order_detail_info_view__comment_view">
        {I18n.tr('components.order.detail.general_comment')}&nbsp;
        <span className="order_detail_info_view__comment">
          {this.order.comment}
        </span>
      </div>
    );
  }

  renderStatus() {
    return (
      <div className="order_detail_info_view__status_label">
        {I18n.tr('components.order.detail.status')} {OrderViewUtils.getStatusLabel(this.order.status)}
      </div>
    );
  }

  renderTotal() {
    if (this.order.isCanceled() || this.order.isFailed()) {
      return null;
    }

    if (!this.order.isPayed()) {
      return (
        <div className="order_detail_info_view__total text-danger">
          {I18n.tr(
              'components.order.detail.order_total')} {CurrencyUtils.formatPriceWithCurrency(
            this.order.getTotalPrice(), this.currencyCode)}
        </div>
      );
    }

    return (
      <div className="order_detail_info_view__total">
        {I18n.tr(
            'components.order.detail.order_total')} {CurrencyUtils.formatPriceWithCurrency(
          this.order.check.total, this.currencyCode)}
      </div>
    );
  }

  renderPaymentStatus() {
    if (this.order.isCanceled() || this.order.isFailed()) {
      return (
        <div className="pull-right">
          <h3 className="text-danger text-uppercase">
            ✖ {OrderViewUtils.getStatusLabel(this.order.status)}
          </h3>
        </div>
      );
    }
    if (!this.order.isPayed()) {
      return null;
    }

    return (
      <div className="pull-right">
        <h3 className="text-success text-uppercase">
         ✔ {I18n.tr('components.order.detail.has_paid')}
        </h3>
      </div>
    );
  }

  renderOnSitePayment() {
    if (this.order.isPayed()) {
      return (null)
    }

    return (
      <div className="order_detail_info_view__on_site_payment">
        {I18n.tr('components.order.detail.on_site_payment')}
      </div>
    );
  }

  renderChangeStatus() {
    // DELIVERED, nothing more to do.
    if (this.order.status === Order.OrderStatus.DELIVERED) {
      return (null);
    }

    const nextStatus = Order.getNextStatusForRestaurateur(
      this.order.status,
      this.props.order.mode
    );

    if (this.state.changingStatus) {
      return (
        <div className="order_detail_info_view__status_change">
          <i className="fa fa-spinner fa-spin" />
        </div>
      );
    }

    return (
      <div className="order_detail_info_view__status_change">
        <OrderStatusStepper
          status={this.order.status}
          orderMode={this.order.mode}
          nextStatus={nextStatus}
          onNextStatus={this.onChangeStatus}
        />
      </div>
    );
  }

  renderPrint() {
    if (!MobileAppInteractor.printerIsAvailable()) {
      return (null);
    }

    return (
      <div>
        <button
          className="btn"
          onClick={this.onPrintOrder}
        >
          <i className="fa fa-print" /> {I18n.tr('order.print')}
        </button>
      </div>
    );
  }

  render() {
    return (
      <div className="order_detail_info_view row">

        <div className="col-xs-12">
          {this.renderHeaderMode()}
        </div>

        <div className="col-xs-12">
          {this.renderPaymentStatus()}

          {this.renderClientInfo()}

          {this.renderDeliveryAddress()}

          {this.renderTotal()}

          {this.renderOnSitePayment()}

          {this.renderComment()}

          {this.renderPrint()}

        </div>

        {/* <div className="col-xs-12">
          {this.renderChangeStatus()}
        </div> */}
      </div>
    );
  }

}
